import { useState, useEffect } from 'react';

/**
 * Cargador dinámico de Google Maps API que usa variables de entorno
 * Similar a como lo hace Vue - carga desde JavaScript, no desde HTML
 */

let googleMapsPromise: Promise<void> | null = null;
let isLoaded = false;

export interface GoogleMapsLoaderOptions {
  apiKey?: string;
  version?: string;
  libraries?: string[];
}

/**
 * Cargar Google Maps API dinámicamente usando variables de entorno
 */
export const loadGoogleMapsAPI = (options: GoogleMapsLoaderOptions = {}): Promise<void> => {
  // Si ya está cargado, retornar inmediatamente
  if (isLoaded && typeof google !== 'undefined' && google.maps) {
    return Promise.resolve();
  }

  // Si ya hay una promesa en progreso, retornarla
  if (googleMapsPromise) {
    return googleMapsPromise;
  }

  const {
    apiKey = (import.meta.env.VITE_GOOGLE_MAPS_API_KEY_OVERRIDE as string) ||
             (window as any).__GOOGLE_MAPS_API_KEY__ ||
             import.meta.env.VITE_GOOGLE_MAPS_API_KEY ||
             (window as any).__VITE_GOOGLE_MAPS_API_KEY__ ||
             'AIzaSyCsgVgYItZbc_7aFCoPZDoFc8eaU-08d64', // Fallback final
    version = 'weekly',
    libraries: _libraries = ['places', 'geometry']
  } = options;

  // console.log('🔍 Cargando Google Maps con configuración:', {
  //  apiKeyPresent: !!apiKey,
  //  apiKeyPreview: apiKey ? `${apiKey.substring(0, 10)}...` : 'NO_API_KEY',
  //  version,
  //  libraries,
  //  envCheck: import.meta.env.VITE_GOOGLE_MAPS_API_KEY
  // });

  // Validar API Key
  if (!apiKey) {
    const error = new Error('Google Maps API Key no configurada. Verifica VITE_GOOGLE_MAPS_API_KEY en .env');
    console.error('❌', error.message);
    return Promise.reject(error);
  }

  // Crear promesa de carga
  googleMapsPromise = new Promise((resolve, reject) => {
    // Verificar si ya existe el script
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      // console.log('🔄 Script de Google Maps ya existe, esperando carga...');
      existingScript.addEventListener('load', () => {
        isLoaded = true;
        resolve();
      });
      existingScript.addEventListener('error', reject);
      return;
    }

    try {
      // Usar el loader oficial de Google (versión moderna)
      const loaderScript = document.createElement('script');
      loaderScript.innerHTML = `
        (g=>{var h,a,k,p="The Google Maps JavaScript API",c="google",l="importLibrary",q="__ib__",m=document,b=window;b=b[c]||(b[c]={});var d=b.maps||(b.maps={}),r=new Set,e=new URLSearchParams,u=()=>h||(h=new Promise(async(f,n)=>{await (a=m.createElement("script"));e.set("libraries",[...r]+"");for(k in g)e.set(k.replace(/[A-Z]/g,t=>"_"+t[0].toLowerCase()),g[k]);e.set("callback",c+".maps."+q);a.src=\`https://maps.\${c}apis.com/maps/api/js?\`+e;d[q]=f;a.onerror=()=>h=n(Error(p+" could not load."));a.nonce=m.querySelector("script[nonce]")?.nonce||"";m.head.append(a)}));d[l]?console.warn(p+" only loads once. Ignoring:",g):d[l]=(f,...n)=>r.add(f)&&u().then(()=>d[l](f,...n))})({
          key: "${apiKey}",
          v: "${version}"
        });
      `;

      // Ejecutar el loader
      document.head.appendChild(loaderScript);

      // Polling para verificar cuándo está disponible
      const checkAvailability = () => {
        if (typeof google !== 'undefined' && google.maps && (google.maps as any).importLibrary) {
          isLoaded = true;
          // console.log('✅ Google Maps API cargada exitosamente:', google.maps.version);
          resolve();
        } else {
          // Reintentar en 100ms
          setTimeout(checkAvailability, 100);
        }
      };

      // Comenzar verificación después de un breve delay
      setTimeout(checkAvailability, 500);

      // Timeout de seguridad
      setTimeout(() => {
        if (!isLoaded) {
          reject(new Error('Timeout cargando Google Maps API'));
        }
      }, 10000);

    } catch (error) {
      console.error('❌ Error cargando Google Maps:', error);
      reject(error);
    }
  });

  return googleMapsPromise;
};

/**
 * Hook de React para cargar Google Maps API
 */
export const useGoogleMapsLoader = (options: GoogleMapsLoaderOptions = {}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isReady) return;

    setIsLoading(true);
    setError(null);

    loadGoogleMapsAPI(options)
      .then(() => {
        setIsReady(true);
        setIsLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setIsLoading(false);
      });
  }, []);

  return { isLoading, isReady, error };
};